'use client';

import { addAmbassadorCollaboration } from '@/api/activity';
import ActionSheet from '@/components/ActionSheet';
import Button from '@/components/Button';
import AutoComplete from '@/components/Form/AutoComplete';
import Input from '@/components/Form/FormInput';
import PhoneInput from '@/components/Form/PhoneInput';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { toast } from '@/components/Toast';
import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { font } from '@/theme/zeelool.theme';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import { parsePhoneValue } from '@/utils/tools';
import { Box } from '@mui/material';
import { RightArrowOutlined } from '@zeelool/icon';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import FormItem from './FormItem';
import { useTranslations } from '@/i18n/use-translations';

// 类型定义
interface SocialAccount {
  account_name: string;
  handle: string;
}

interface FormData {
  name: string;
  phone: string;
  business_email: string;
  influencer_info: SocialAccount[];
}

interface CountryData {
  country_id: string;
  tel_code: string;
}

interface AmbassadorRegistFormProps {
  deviceType: EPlatform;
  countryData: CountryData[];
}

const socialPlatforms = ['Instagram', 'Facebook', 'Tiktok', 'Twitch', 'Youtube', 'Blog'];

export default function AmbassadorRegistForm({
  deviceType,
  countryData,
}: AmbassadorRegistFormProps) {
  const t = useTranslations('ambassador.ambassador-regist');
  const [submitLoading, setSubmitLoading] = useState(false);

  // 埋点相关 - 参考AmbassadorContent.tsx的做法
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();
  // ActionSheet 状态管理
  const [showSocialPlatformSheet, setShowSocialPlatformSheet] = useState(false);
  const [currentSocialIndex, setCurrentSocialIndex] = useState(0);

  const methods = useForm<FormData>({
    defaultValues: {
      name: '',
      phone: '',
      business_email: '',
      influencer_info: [{ account_name: '', handle: '' }],
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  // 添加社交账号
  const addSocialAccount = () => {
    const currentAccounts = watch('influencer_info');
    if (currentAccounts.length < 5) {
      setValue('influencer_info', [...currentAccounts, { account_name: '', handle: '' }]);
    }
  };

  // 删除社交账号
  const removeSocialAccount = (index: number) => {
    const currentAccounts = watch('influencer_info');
    if (currentAccounts.length > 1) {
      const newAccounts = currentAccounts.filter((_, i) => i !== index);
      setValue('influencer_info', newAccounts);
    }
  };

  // 邮箱验证
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 按钮点击埋点
  const trackButtonClick = (btnName: string) => {
    try {
      trackEvent(
        {
          eventName: 'AmbassadorJoinPageBtn_Click',
          properties: {
            btn_name: btnName,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('AmbassadorJoinPageBtn_Click tracking error:', e);
    }
  };

  // 处理按钮点击事件（包含埋点）
  const handleJoinUsClick = () => {
    // 触发按钮点击埋点
    trackButtonClick('Join Us Now');
    // 触发表单提交
    handleSubmit(onSubmit)();
  };

  // 提交表单
  const onSubmit = async (data: FormData) => {
    try {
      setSubmitLoading(true);

      // 从完整的电话号码中解析出国家代码和电话号码
      const { telCode, telephone } = parsePhoneValue(data.phone);
      const phoneCode = telCode; // 保持完整的国家代码格式，如 "AF(+93)"
      const numericCode = telCode.replace(/\D/g, ''); // 提取数字部分，如 "93"

      // 转换数据格式
      const apiData = {
        ...data,
        phone: `${numericCode}-${telephone}`,
        phoneCode,
      };

      // 调用API
      const response = await addAmbassadorCollaboration(apiData);

      if (response.code === 200) {
        toast.success(t('submitSuccess'));

        // 重置表单
        methods.reset();
      } else {
        throw new Error(response.msg || 'Submit failed');
      }
    } catch (error) {
      toast.error(t('submitFailed'));

      logger.error('提交失败:', error);
    } finally {
      setSubmitLoading(false);
    }
  };

  // 页面浏览埋点 - 参考AmbassadorContent.tsx的做法
  const trackPageView = () => {
    try {
      const { refer_page_name, refer_url_path } = getFromRouterInfo();
      trackEvent(
        {
          eventName: 'AmbassadorJoinPage_View',
          properties: {
            refer_page_name,
            refer_url_path,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('AmbassadorJoinPage_View tracking error:', e);
    }
  };

  // 页面加载时触发页面浏览埋点
  useEffect(() => {
    trackPageView();
  }, []);

  // App相关状态
  const inAppH5 = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);
  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);

  // 在App内嵌环境时初始化通信
  useEffect(() => {
    if (inAppH5) {
      handleAppCommunication();
    }
  }, [inAppH5]);

  // 处理App通信逻辑
  const handleAppCommunication = async () => {
    try {
      // 注册token回调通信
      appCommunicationManager.registerCallback(
        EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
        async (token) => {
          logger.log('App登录token:', token);
          if (token !== 'ClearToken') {
            setToken(token);
            // 获取用户信息
            await refreshAccountInfo();
          } else {
            // 清空当前用户信息
            setToken('');
            setUserInfo({});
          }
        },
        {},
        'BackTokenInfo' as any, // 使用BackTokenInfo的postMessage方法
      );
    } catch (error) {
      logger.error('App通信初始化失败:', error);
    }
  };

  // 组件销毁时清理App通信管理器
  useEffect(() => {
    return () => {
      if (inAppH5) {
        appCommunicationManager.cleanup();
      }
    };
  }, [inAppH5]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box className="mx-auto w-zuto sm:w-fit px-[14px]">
          {/* 姓名 */}
          <FormItem label={t('fullNameLabel')} required>
            <Input
              placeholder={t('fullNamePlaceholder')}
              name="name"
              control={control}
              formItemClassName="mb-[24px]"
              rules={{
                required: t('fullNamePlaceholder'),
                maxLength: { value: 200, message: 'Maximum 200 characters' },
              }}
            />
          </FormItem>

          {/* 电话 */}
          <FormItem label={t('phoneLabel')}>
            <Box
              sx={{
                '& .MuiFormHelperText-root': {
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                },
              }}
            >
              <PhoneInput
                name="phone"
                placeholder={t('phonePlaceholder')}
                control={control}
                formItemClassName="mb-[24px]"
                countryList={countryData
                  .filter((country) => country.country_id && country.tel_code)
                  .map((country) => ({
                    country_id: country.country_id!,
                    tel_code: country.tel_code!,
                    country: country.country_id!,
                  }))}
              />
            </Box>
          </FormItem>

          {/* 邮箱 */}
          <FormItem label={t('emailLabel')} required>
            <Input
              name="business_email"
              control={control}
              placeholder={t('emailPlaceholder')}
              formItemClassName="mb-[24px]"
              rules={{
                required: t('emailPlaceholder'),
                maxLength: { value: 200, message: 'Maximum 200 characters' },
                validate: (value) => validateEmail(value) || 'Please enter a valid email address',
              }}
            />
          </FormItem>

          {/* 社交账号 */}
          {watch('influencer_info').map((account, index) => (
            <Box key={index}>
              {index > 0 && (
                <Box className="text-right mb-[24px]">
                  <button
                    type="button"
                    onClick={() => removeSocialAccount(index)}
                    className="text-sm text-[#282828] hover:text-[#666666] cursor-pointer"
                  >
                    {t('removeSocialAccount')}
                  </button>
                </Box>
              )}

              <FormItem
                label={t('socialAccountsLabel')}
                required
                onClick={
                  deviceType !== EPlatform.WEB
                    ? () => {
                        setCurrentSocialIndex(index);
                        setShowSocialPlatformSheet(true);
                      }
                    : undefined
                }
              >
                <WebOnly>
                  <Box className="flex-1 mb-[24px]">
                    <AutoComplete
                      name={`influencer_info.${index}.account_name`}
                      layout="horizontal"
                      control={control}
                      options={socialPlatforms}
                      placeholder={t('socialAccountsPlaceholder')}
                      rules={{
                        required: t('socialAccountsPlaceholder'),
                      }}
                      disableClearable
                      getOptionLabel={(option) => option}
                    />
                  </Box>
                </WebOnly>

                <WapOnly>
                  <Box className="relative">
                    <Box
                      className={`flex items-center justify-between w-full h-[46px] border rounded-[6px] px-[16px] cursor-pointer mb-[24px] mt-[8px] ${
                        errors.influencer_info?.[index]?.account_name
                          ? 'border-error'
                          : 'border-[#d4d4d4]'
                      }`}
                    >
                      <div
                        className={
                          !watch(`influencer_info.${index}.account_name`)
                            ? 'text-[#808080]'
                            : 'text-[#282828]'
                        }
                      >
                        {watch(`influencer_info.${index}.account_name`) || 'Select platform'}
                      </div>
                      <RightArrowOutlined className="text-[16px] text-auxiliary-text" />
                    </Box>
                    {/* 移动端校验错误显示 */}
                    <Controller
                      name={`influencer_info.${index}.account_name`}
                      control={control}
                      rules={{
                        required: 'Please select a social accounts handle',
                      }}
                      render={({ fieldState: { error } }) => (
                        <>
                          {error && (
                            <div className="text-error text-[12px] mt-[-20px] mb-[8px]">
                              {error.message}
                            </div>
                          )}
                        </>
                      )}
                    />
                  </Box>
                </WapOnly>
              </FormItem>

              <FormItem label={t('handleLabel')} required>
                <Input
                  name={`influencer_info.${index}.handle`}
                  control={control}
                  placeholder={t('handlePlaceholder')}
                  formItemClassName="mb-[32px]"
                  rules={{
                    required: t('handlePlaceholder'),
                    maxLength: { value: 200, message: 'Maximum 200 characters' },
                  }}
                  slotProps={{
                    htmlInput: {
                      maxLength: 200,
                    },
                  }}
                />
              </FormItem>
            </Box>
          ))}

          {/* 添加社交账号按钮 */}
          {watch('influencer_info').length < 5 && (
            <Box className="mb-[24px] sm:ml-[250px]">
              <button
                type="button"
                onClick={addSocialAccount}
                className="flex items-center justify-center w-[190px] h-[46px] sm:w-[200px] sm:h-[40px] bg-white border border-[#282828] text-[#282828] rounded-[42px] sm:rounded-[6px] text-[14px] font-normal hover:bg-[rgba(128,128,128,0.05)] cursor-pointer mt-[8px]"
              >
                <span className="mr-1">+</span>
                {t('addSocialAccount')}
              </button>
            </Box>
          )}

          {/* 提交按钮 */}
          <WebOnly>
            <Box className="mt-[40px] mb-[200px] ml-[245px]">
              <Button
                className="w-[300px] h-[48px]"
                radiusSize="small"
                color="primary"
                variant="contained"
                sx={{
                  fontSize: '16px',
                  fontFamily: font.bb,
                  backgroundColor: '#282828',
                  borderRadius: '42px',
                  '&:hover': {
                    backgroundColor: '#1a1a1a',
                  },
                }}
                loading={submitLoading}
                onClick={handleJoinUsClick}
              >
                {t('joinUsNow')}
              </Button>
            </Box>
          </WebOnly>

          <WapOnly>
            <Box className="mt-[20px] mb-[80px]">
              <Button
                className="w-full h-[46px]"
                radiusSize="large"
                color="primary"
                variant="contained"
                sx={{
                  fontSize: '14px',
                  fontFamily: font.bb,
                  backgroundColor: '#282828',
                  borderRadius: '23px',
                  '&:hover': {
                    backgroundColor: '#1a1a1a',
                  },
                }}
                loading={submitLoading}
                onClick={handleJoinUsClick}
              >
                {t('joinUsNow')}
              </Button>
            </Box>
          </WapOnly>
        </Box>
      </form>

      {/* 移动端社交平台选择弹层 */}
      <WapOnly>
        <ActionSheet
          open={showSocialPlatformSheet}
          title="Social Accounts handle"
          options={socialPlatforms.map((platform) => ({
            label: platform,
            value: platform,
          }))}
          onSave={(value) => {
            setValue(`influencer_info.${currentSocialIndex}.account_name`, value);
            setShowSocialPlatformSheet(false);
          }}
          onClose={() => setShowSocialPlatformSheet(false)}
          value={watch(`influencer_info.${currentSocialIndex}.account_name`) || ''}
        />
      </WapOnly>
    </FormProvider>
  );
}
